import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const TeacherForm = ({ formData, onFormChange, onSubmit, isEditing = false }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: 'black' }}>
          Basic Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
            <FormControl fullWidth required>
              <InputLabel>Employment Status</InputLabel>
              <Select
                value={formData.employment_status}
                label="Employment Status"
                onChange={(e) => onFormChange({ employment_status: e.target.value })}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="On Leave">On Leave</MenuItem>
                <MenuItem value="Suspended">Suspended</MenuItem>
                <MenuItem value="Retired">Retired</MenuItem>
                <MenuItem value="Terminated">Terminated</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: 'black' }}>
          Personal Details
        </Typography>
        <Grid container spacing={3}>
          <Grid item size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
            <TextField
              fullWidth
              label="First Name"
              value={formData.first_name}
              onChange={(e) => onFormChange({ first_name: e.target.value })}
              required
            />
          </Grid>
          <Grid item size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
            <TextField
              fullWidth
              label="Middle Name"
              value={formData.middle_name}
              onChange={(e) => onFormChange({ middle_name: e.target.value })}
            />
          </Grid>
          <Grid item size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
            <TextField
              fullWidth
              label="Last Name"
              value={formData.last_name}
              onChange={(e) => onFormChange({ last_name: e.target.value })}
              required
            />
          </Grid>
          <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
            <FormControl fullWidth required>
              <InputLabel>Gender</InputLabel>
              <Select
                value={formData.gender}
                label="Gender"
                onChange={(e) => onFormChange({ gender: e.target.value })}
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date of Birth"
                value={formData.date_of_birth ? dayjs(formData.date_of_birth) : null}
                onChange={(newValue) => onFormChange({ date_of_birth: newValue ? newValue.format('YYYY-MM-DD') : '' })}
                slotProps={{ textField: { fullWidth: true, size: "medium" } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
            <TextField
              fullWidth
              label="National ID"
              value={formData.national_id}
              onChange={(e) => onFormChange({ national_id: e.target.value })}
            />
          </Grid>
          <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Employment Date"
                value={formData.employment_date ? dayjs(formData.employment_date) : null}
                onChange={(newValue) => onFormChange({ employment_date: newValue ? newValue.format('YYYY-MM-DD') : '' })}
                slotProps={{ textField: { fullWidth: true, size: "medium" } }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: 'black' }}>
          Contact Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone_number}
              onChange={(e) => onFormChange({ phone_number: e.target.value })}
              required
            />
          </Grid>
          <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
            <TextField
              fullWidth
              type="email"
              label="Email (Optional)"
              value={formData.email}
              onChange={(e) => onFormChange({ email: e.target.value })}
            />
          </Grid>
          <Grid item size={{ lg: 12, md: 12, sm: 12, xs: 12 }}>
            <TextField
              fullWidth
              label="Address"
              value={formData.address}
              onChange={(e) => onFormChange({ address: e.target.value })}
              multiline
              rows={3}
              placeholder="Enter complete address..."
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: 'black' }}>
          Professional Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
            <TextField
              fullWidth
              label="Qualification"
              value={formData.qualification}
              onChange={(e) => onFormChange({ qualification: e.target.value })}
              placeholder="e.g., Bachelor of Education"
            />
          </Grid>
          <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
            <TextField
              fullWidth
              label="Specialization"
              value={formData.specialization}
              onChange={(e) => onFormChange({ specialization: e.target.value })}
              placeholder="e.g., Mathematics, English"
            />
          </Grid>
        </Grid>
      </Box>

      {!isEditing && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: 'black' }}>
            User Account
          </Typography>
          <Grid container spacing={3}>
            <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
              <TextField
                fullWidth
                type={showPassword ? "text" : "password"}
                label="Password"
                value={formData.password}
                onChange={(e) => onFormChange({ password: e.target.value })}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
              <TextField
                fullWidth
                type={showConfirmPassword ? "text" : "password"}
                label="Confirm Password"
                value={formData.password_confirmation}
                onChange={(e) => onFormChange({ password_confirmation: e.target.value })}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default TeacherForm;
